import React, { useEffect, useState, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  Folder, File as FileIcon, FolderPlus, Upload, Download, Pencil, Trash2,
  ChevronRight, Home, Loader2, Save, X, Lock, HardDrive,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import api from '../../lib/api';

const fmtSize = (b) => {
  if (b === 0) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

const fmCfg = (extra = {}) => ({
  ...extra,
  headers: { 'X-FM-Password': sessionStorage.getItem('fm_pwd') || '' },
});

export const FileManagerTab = ({ isItalian }) => {
  const [unlocked, setUnlocked] = useState(() => !!sessionStorage.getItem('fm_pwd'));
  const [pwdInput, setPwdInput] = useState('');
  const [unlocking, setUnlocking] = useState(false);
  const [path, setPath] = useState('');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorFile, setEditorFile] = useState(null);
  const [editorContent, setEditorContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [renaming, setRenaming] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [newFolderMode, setNewFolderMode] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(async (p = path) => {
    setLoading(true);
    try {
      const res = await api.get('/admin/files/list', fmCfg({ params: { path: p } }));
      setEntries(res.data.entries);
      setPath(res.data.path);
    } catch (err) {
      if (err?.response?.status === 401) {
        sessionStorage.removeItem('fm_pwd');
        setUnlocked(false);
      }
      toast.error(err?.response?.data?.detail || 'Errore caricamento');
    } finally {
      setLoading(false);
    }
  }, [path]);

  useEffect(() => { if (unlocked) load(''); }, [unlocked]); // eslint-disable-line react-hooks/exhaustive-deps

  const tryUnlock = async () => {
    if (!pwdInput.trim() || unlocking) return;
    setUnlocking(true);
    try {
      await api.get('/admin/files/list', { params: { path: '' }, headers: { 'X-FM-Password': pwdInput } });
      sessionStorage.setItem('fm_pwd', pwdInput);
      setPwdInput('');
      setUnlocked(true);
    } catch (err) {
      toast.error(err?.response?.status === 401
        ? (isItalian ? 'Password errata' : 'Wrong password')
        : (err?.response?.data?.detail || 'Errore'));
    } finally {
      setUnlocking(false);
    }
  };

  const openEntry = async (e) => {
    if (e.is_dir) { load(e.path); return; }
    if (!e.editable) {
      toast.info(isItalian ? 'File binario o troppo grande: usa il download' : 'Binary/large file: use download');
      return;
    }
    try {
      const res = await api.get('/admin/files/read', fmCfg({ params: { path: e.path } }));
      setEditorFile(e);
      setEditorContent(res.data.content);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore lettura');
    }
  };

  const saveFile = async () => {
    setSaving(true);
    try {
      await api.post('/admin/files/write', { path: editorFile.path, content: editorContent }, fmCfg());
      toast.success(isItalian ? 'File salvato' : 'File saved');
      setEditorFile(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const download = async (e) => {
    try {
      const res = await api.get('/admin/files/download', fmCfg({ params: { path: e.path }, responseType: 'blob' }));
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = e.name;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Errore download');
    }
  };

  const doRename = async (e) => {
    if (!renameValue.trim()) return;
    try {
      await api.post('/admin/files/rename', { path: e.path, new_name: renameValue.trim() }, fmCfg());
      toast.success(isItalian ? 'Rinominato' : 'Renamed');
      setRenaming(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore');
    }
  };

  const doDelete = async (e) => {
    try {
      await api.delete('/admin/files', fmCfg({ params: { path: e.path } }));
      toast.success(isItalian ? 'Eliminato' : 'Deleted');
      setConfirmDelete(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore');
    }
  };

  const doMkdir = async () => {
    if (!newFolderName.trim()) return;
    try {
      await api.post('/admin/files/mkdir', { path, name: newFolderName.trim() }, fmCfg());
      setNewFolderMode(false);
      setNewFolderName('');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore');
    }
  };

  const doUpload = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error(isItalian ? 'File troppo grande (max 25 MB)' : 'File too large (max 25 MB)');
      return;
    }
    setUploading(true);
    try {
      const b64 = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result.split(',')[1]);
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      await api.post('/admin/files/upload', { path, name: file.name, content_b64: b64 }, fmCfg());
      toast.success(isItalian ? `"${file.name}" caricato` : `"${file.name}" uploaded`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore upload');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const crumbs = path ? path.split('/') : [];

  if (!unlocked) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center justify-center py-20" data-testid="file-manager-lock">
        <div className="bg-[#0a0d15] border border-white/10 p-8 max-w-sm w-full text-center space-y-4">
          <Lock className="mx-auto text-[#FF9100]" size={32} />
          <h3 className="text-white font-heading uppercase tracking-wider text-sm">
            {isItalian ? 'File Manager Protetto' : 'File Manager Locked'}
          </h3>
          <p className="text-xs text-slate-400">
            {isItalian
              ? 'Inserisci la password di sicurezza per accedere ai file del server.'
              : 'Enter the security password to access server files.'}
          </p>
          <Input
            type="password"
            value={pwdInput}
            onChange={(e) => setPwdInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && tryUnlock()}
            placeholder="Password"
            className="bg-[#050508] border-white/10 text-white text-center"
            data-testid="fm-password-input"
            autoFocus
          />
          <Button
            onClick={tryUnlock}
            disabled={unlocking || !pwdInput.trim()}
            className="w-full bg-[#FF9100]/20 border border-[#FF9100]/40 text-[#FF9100] hover:bg-[#FF9100]/30"
            data-testid="fm-unlock-btn"
          >
            {unlocking ? <Loader2 size={16} className="animate-spin" /> : (isItalian ? 'Sblocca' : 'Unlock')}
          </Button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4" data-testid="file-manager-tab">
      <div className="bg-[#FF9100]/10 border border-[#FF9100]/30 rounded p-3 text-xs text-[#FF9100]">
        ⚠️ {isItalian
          ? 'Attenzione: stai modificando i file reali del server. Le modifiche al codice possono rompere il sito e vengono PERSE a ogni nuovo deploy.'
          : 'Warning: you are editing real server files. Code changes may break the site and are LOST on every new deploy.'}
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1 text-sm text-slate-300 flex-1 min-w-0 overflow-x-auto">
          <button className="flex items-center gap-1 hover:text-[#00F0FF] shrink-0" onClick={() => load('')} data-testid="fm-breadcrumb-root">
            <Home size={14} /> app
          </button>
          {crumbs.map((c, i) => (
            <React.Fragment key={i}>
              <ChevronRight size={12} className="text-slate-600 shrink-0" />
              <button className="hover:text-[#00F0FF] shrink-0" onClick={() => load(crumbs.slice(0, i + 1).join('/'))}>
                {c}
              </button>
            </React.Fragment>
          ))}
        </div>
        <Button size="sm" variant="outline" className="border-white/15 text-slate-300 h-8" onClick={() => setNewFolderMode(true)} data-testid="fm-new-folder-btn">
          <FolderPlus size={14} className="mr-1" /> {isItalian ? 'Cartella' : 'Folder'}
        </Button>
        <Button size="sm" className="bg-[#00F0FF]/15 border border-[#00F0FF]/40 text-[#00F0FF] hover:bg-[#00F0FF]/25 h-8" disabled={uploading} onClick={() => fileInputRef.current?.click()} data-testid="fm-upload-btn">
          {uploading ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Upload size={14} className="mr-1" />} Upload
        </Button>
        <input ref={fileInputRef} type="file" className="hidden" onChange={(e) => doUpload(e.target.files?.[0])} data-testid="fm-upload-input" />
      </div>

      {newFolderMode && (
        <div className="flex items-center gap-2">
          <Input value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && doMkdir()} placeholder={isItalian ? 'Nome cartella' : 'Folder name'} className="h-8 bg-[#050508] border-white/10 max-w-xs" data-testid="fm-new-folder-input" />
          <Button size="sm" className="h-8" onClick={doMkdir} data-testid="fm-new-folder-confirm">OK</Button>
          <Button size="sm" variant="ghost" className="h-8" onClick={() => setNewFolderMode(false)}><X size={14} /></Button>
        </div>
      )}

      {/* File list */}
      <div className="bg-[#0a0d15] border border-white/10 rounded overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-10 text-slate-400 text-sm">
            <Loader2 size={16} className="animate-spin" /> {isItalian ? 'Caricamento…' : 'Loading…'}
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {entries.map((e) => (
              <div key={e.path} className="flex items-center gap-3 px-4 py-2 hover:bg-white/5 group" data-testid={`fm-entry-${e.name}`}>
                <button className="flex items-center gap-3 flex-1 min-w-0 text-left" onClick={() => openEntry(e)}>
                  {e.is_dir
                    ? <Folder size={16} className="text-[#FFEA00] shrink-0" />
                    : <FileIcon size={16} className={`shrink-0 ${e.editable ? 'text-[#00F0FF]' : 'text-slate-500'}`} />}
                  <span className="text-sm text-white truncate">{e.name}</span>
                  {e.protected && <Lock size={11} className="text-slate-600 shrink-0" />}
                </button>
                <span className="text-xs text-slate-500 font-mono w-20 text-right shrink-0">{e.is_dir ? '' : fmtSize(e.size)}</span>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                  {!e.is_dir && (
                    <button className="p-1.5 text-slate-400 hover:text-white rounded" onClick={() => download(e)} title="Download" data-testid={`fm-download-${e.name}`}>
                      <Download size={13} />
                    </button>
                  )}
                  {!e.protected && (
                    <>
                      <button className="p-1.5 text-slate-400 hover:text-white rounded" onClick={() => { setRenaming(e.path); setRenameValue(e.name); setConfirmDelete(null); }} title={isItalian ? 'Rinomina' : 'Rename'} data-testid={`fm-rename-${e.name}`}>
                        <Pencil size={13} />
                      </button>
                      <button className="p-1.5 text-red-400/70 hover:text-red-400 rounded" onClick={() => { setConfirmDelete(e.path); setRenaming(null); }} title={isItalian ? 'Elimina' : 'Delete'} data-testid={`fm-delete-${e.name}`}>
                        <Trash2 size={13} />
                      </button>
                    </>
                  )}
                </div>
                {renaming === e.path && (
                  <div className="flex items-center gap-1 shrink-0">
                    <Input value={renameValue} onChange={(ev) => setRenameValue(ev.target.value)} onKeyDown={(ev) => ev.key === 'Enter' && doRename(e)} className="h-7 w-44 bg-[#050508] border-white/10 text-xs" data-testid="fm-rename-input" />
                    <button className="p-1 text-emerald-400" onClick={() => doRename(e)} data-testid="fm-rename-confirm"><Save size={13} /></button>
                    <button className="p-1 text-slate-400" onClick={() => setRenaming(null)}><X size={13} /></button>
                  </div>
                )}
                {confirmDelete === e.path && (
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className="text-xs text-red-300">{isItalian ? 'Eliminare?' : 'Delete?'}</span>
                    <button className="px-2 py-0.5 text-xs bg-red-500/15 border border-red-500/40 text-red-300 rounded" onClick={() => doDelete(e)} data-testid="fm-delete-confirm">
                      {isItalian ? 'Sì' : 'Yes'}
                    </button>
                    <button className="px-2 py-0.5 text-xs bg-white/5 border border-white/15 text-slate-300 rounded" onClick={() => setConfirmDelete(null)}>
                      {isItalian ? 'No' : 'No'}
                    </button>
                  </div>
                )}
              </div>
            ))}
            {entries.length === 0 && (
              <p className="text-center text-slate-500 text-sm py-8">{isItalian ? 'Cartella vuota' : 'Empty folder'}</p>
            )}
          </div>
        )}
      </div>

      {/* Editor modal */}
      {editorFile && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="fm-editor-modal">
          <div className="bg-[#0a0d15] border border-white/15 rounded-lg w-full max-w-5xl h-[85vh] flex flex-col">
            <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
              <HardDrive size={15} className="text-[#00F0FF]" />
              <p className="text-sm font-mono text-white truncate flex-1">{editorFile.path}</p>
              <Button size="sm" className="h-8 bg-[#00E676]/15 border border-[#00E676]/40 text-[#00E676] hover:bg-[#00E676]/25" disabled={saving} onClick={saveFile} data-testid="fm-editor-save">
                {saving ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Save size={14} className="mr-1" />}
                {isItalian ? 'Salva' : 'Save'}
              </Button>
              <Button size="sm" variant="ghost" className="h-8 text-slate-400" onClick={() => setEditorFile(null)} data-testid="fm-editor-close">
                <X size={15} />
              </Button>
            </div>
            <textarea
              value={editorContent}
              onChange={(e) => setEditorContent(e.target.value)}
              spellCheck={false}
              className="flex-1 w-full bg-[#050508] text-slate-200 font-mono text-[13px] leading-relaxed p-4 outline-none resize-none"
              data-testid="fm-editor-textarea"
            />
          </div>
        </div>
      )}
    </motion.div>
  );
};
